import { Badge, Box, IconButton, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import React, { useCallback, useMemo } from "react";
import CommentIcon from "@mui/icons-material/Comment";
import { useComments } from "../../pages/Receiving/hooks/useComments";
import { CommentModal } from "../Modal/CommentModal";
import { ReceiveComment } from "../../common/Receive";

interface ReceiveCommentsProps {
	receiveId?: string | number | null;
	invoiceId?: string | number | null;
	transferId?: string | null;
	initialComments?: ReceiveComment[];
	compact?: boolean;
}

const useStyles = makeStyles({
	customYellowBadge: {
		backgroundColor: "yellow",
		color: "black",
		left: 30,
		bottom: 5,
	},
	disabledIcon: {
		color: "#bdbdbd",
		opacity: 0.6,
	},
});

const validSourceSystems = ["Invoice", "ReceivingApp"];

const ReceiveComments: React.FC<ReceiveCommentsProps> = ({
	receiveId,
	invoiceId,
	transferId,
	initialComments = [],
	compact = false,
}) => {
	const classes = useStyles();
	const isDisabled = !receiveId && !invoiceId && !transferId;

	const {
		comments,
		commentModal,
		onCommentModalOpen,
		onCommentModalClose,
		onAddComment,
		onAddInvoiceComment,
		onAddTransferComment,
	} = useComments(initialComments);

	const numberOfComments = useMemo(
		() => comments?.filter((c) => validSourceSystems.includes(c.sourceSystem) || !c.sourceSystem).length,
		[comments]
	);

	const getAddCommentHandler = useCallback(() => {
		if (transferId) return onAddTransferComment(transferId);
		if (invoiceId) return onAddInvoiceComment(invoiceId?.toString() ?? "");
		return onAddComment(receiveId?.toString() ?? "");
	}, [transferId, invoiceId, receiveId, onAddTransferComment, onAddInvoiceComment, onAddComment]);

	const tooltipText = `View comment history (${numberOfComments || 0} comments)`;

	const button = (
		<IconButton
			onClick={onCommentModalOpen}
			title={tooltipText}
			sx={{
				color: "text.primary",
				padding: compact ? { xs: 1, sm: 1.25 } : 0.5,
				...(compact && { marginBottom: 0, paddingLeft: 0 }),
			}}
		>
			<CommentIcon
				fontSize={compact ? undefined : "medium"}
				sx={compact ? { fontSize: { xs: "1.25rem", sm: "1.5rem" } } : undefined}
			/>
			<Typography
				variant={compact ? "body2" : "subtitle1"}
				sx={{
					fontWeight: "bold",
					paddingLeft: compact ? { xs: 0.5, sm: 1 } : 0.5,
					...(compact && { fontSize: { xs: "0.875rem", sm: "1rem" } }),
				}}
			>
				Comments
			</Typography>
		</IconButton>
	);

	return (
		<Box display="flex" alignItems="center" sx={{ p: compact ? 0 : 0.5 }}>
			{(numberOfComments ?? 0) > 0 ? (
				<Badge
					badgeContent={numberOfComments.toString()}
					color="primary"
					overlap="circular"
					anchorOrigin={{
						vertical: "top",
						horizontal: "left",
					}}
					classes={{ badge: classes.customYellowBadge }}
				>
					{button}
				</Badge>
			) : (
				button
			)}
			<CommentModal
				isOpen={commentModal.isOpen}
				isDisabled={isDisabled}
				onClose={onCommentModalClose}
				comments={comments}
				onAddComment={getAddCommentHandler()}
			/>
		</Box>
	);
};

export default ReceiveComments;
